import { tagsService } from '@/services/tagsService';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * TagSelector component - A multi-select typeahead for managing contact tags
 * Supports searching existing tags and creating new ones on the fly.
 */
const TagSelector = ({
  selectedTags = [],
  onChange,
  isLoading = false,
  allowCreation = true,
}) => {
  const { projectId } = useParams();
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search tags as user types or show initial tags on focus
  useEffect(() => {
    if (!isOpen) {
      setSuggestions([]);
      return;
    }

    const fetchTags = async () => {
      setIsSearching(true);
      try {
        let results;
        if (query.trim().length === 0) {
          // Fetch initial tags if query is empty
          const response = await tagsService.getTags(projectId, { limit: 10 });
          results = response.docs || [];
        } else {
          // Search tags as user types
          const response = await tagsService.searchTags(projectId, query);
          results = Array.isArray(response) ? response : response.docs || [];
        }

        // Filter out already selected tags
        const filtered = results.filter(
          tag =>
            !selectedTags.find(
              selected =>
                (typeof selected === 'string' ? selected : selected.name) ===
                tag.name
            )
        );
        setSuggestions(filtered);
      } catch (error) {
        console.error('Error fetching tags:', error);
      } finally {
        setIsSearching(false);
      }
    };

    const timeoutId = setTimeout(fetchTags, query.trim() ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [query, projectId, selectedTags, isOpen]);

  const handleAddTag = tag => {
    const tagName = typeof tag === 'string' ? tag : tag.name;
    if (
      !selectedTags.find(t => (typeof t === 'string' ? t : t.name) === tagName)
    ) {
      onChange([...selectedTags, tag]);
    }
    setQuery('');
    setIsOpen(false);
  };

  const handleRemoveTag = tagName => {
    onChange(
      selectedTags.filter(t => (typeof t === 'string' ? t : t.name) !== tagName)
    );
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && query.trim()) {
      e.preventDefault();
      // If there's a suggestion that matches exactly, use it
      const exactMatch = suggestions.find(
        s => s.name.toLowerCase() === query.trim().toLowerCase()
      );

      if (exactMatch) {
        handleAddTag(exactMatch);
      } else if (allowCreation) {
        handleAddTag(query.trim());
      }
    } else if (e.key === 'Backspace' && !query && selectedTags.length > 0) {
      const lastTag = selectedTags[selectedTags.length - 1];
      handleRemoveTag(typeof lastTag === 'string' ? lastTag : lastTag.name);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Tags
      </label>
      <div
        className={`min-h-[42px] p-1.5 border border-gray-300 rounded-md shadow-sm focus-within:ring-1 focus-within:ring-primary-500 focus-within:border-primary-500 flex flex-wrap gap-2 items-center bg-white ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        onClick={() => !isOpen && setIsOpen(true)}
      >
        {selectedTags.map((tag, index) => {
          const name = typeof tag === 'string' ? tag : tag.name;
          const color =
            typeof tag === 'string' ? '#3B82F6' : tag.color || '#3B82F6';
          return (
            <span
              key={index}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white shadow-sm"
              style={{ backgroundColor: color }}
            >
              {name}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  handleRemoveTag(name);
                }}
                className="ml-1.5 inline-flex items-center justify-center h-3 w-3 rounded-full hover:bg-black hover:bg-opacity-20 focus:outline-none"
              >
                <svg
                  className="h-2 w-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  />
                </svg>
              </button>
            </span>
          );
        })}
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={
            selectedTags.length === 0
              ? allowCreation
                ? 'Search or create tags...'
                : 'Search tags...'
              : ''
          }
          className="flex-1 min-w-[120px] outline-none border-none p-0 text-sm focus:ring-0"
          disabled={isLoading}
        />
      </div>

      {isOpen && (query.trim() || suggestions.length > 0) && (
        <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {isSearching ? (
            <div className="px-4 py-2 text-gray-500 italic">
              Searching tags...
            </div>
          ) : (
            <>
              {suggestions.map(tag => (
                <button
                  key={tag._id}
                  onClick={() => handleAddTag(tag)}
                  className="w-full text-left px-4 py-2 hover:bg-primary-50 flex items-center gap-2"
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: tag.color || '#3B82F6' }}
                  />
                  <span>{tag.name}</span>
                </button>
              ))}
              {allowCreation &&
                query.trim() &&
                !suggestions.find(
                  s => s.name.toLowerCase() === query.trim().toLowerCase()
                ) && (
                  <button
                    onClick={() => handleAddTag(query.trim())}
                    className="w-full text-left px-4 py-2 hover:bg-primary-50 text-primary-600 font-medium border-t border-gray-100"
                  >
                    + Create "{query.trim()}"
                  </button>
                )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelector;
